/**
 * Delegation Selector Component
 *
 * Allows delegates to select which clinician they are acting on behalf of.
 * Displays as "Acting as: [Clinician Name]" dropdown.
 */

import { useAuth0 } from '@auth0/auth0-react'
import React, { useEffect, useState } from 'react'
import { useAuthorization } from './authorizationHooks'

interface DelegationOption {
  userId: string
  userName: string
  permissions: string[]
}

interface DelegationSelectorProps {
  /** Optional callback when delegation changes */
  onDelegationChange?: (userId: string | null) => void

  /** Custom styling className */
  className?: string
}

export function DelegationSelector({ onDelegationChange, className = '' }: DelegationSelectorProps) {
  const { activeDelegation, availableDelegations, setActiveDelegation, userId } = useAuthorization()

  const [delegationOptions, setDelegationOptions] = useState<DelegationOption[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // Fetch delegation options (grantors who have delegated to current user)
  useEffect(() => {
    fetchDelegationOptions()
  }, [availableDelegations])

  async function fetchDelegationOptions() {
    try {
      setIsLoading(true)

      console.log('[DelegationSelector] Available delegations:', availableDelegations)
      console.log('[DelegationSelector] Current userId:', userId)

      // Filter delegations where current user is the grantee and status is active
      const activeDelegationsToMe = availableDelegations.filter(
        (d) => d.granteeUserId === userId && d.status === 'active'
      )

      console.log('[DelegationSelector] Active delegations to me:', activeDelegationsToMe)

      // Map to display options with names
      const options: DelegationOption[] = activeDelegationsToMe.map((d) => ({
        userId: d.grantorUserId,
        userName: d.grantorName || d.grantorEmail || d.grantorUserId, // Use name, fallback to email, then ID
        permissions: d.permissions,
      }))

      console.log('[DelegationSelector] Delegation options:', options)
      setDelegationOptions(options)
    } catch (error) {
      console.error('Failed to fetch delegation options:', error)
    } finally {
      setIsLoading(false)
    }
  }

  function handleDelegationChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const selectedUserId = event.target.value === '' ? null : event.target.value
    setActiveDelegation(selectedUserId)

    if (onDelegationChange) {
      onDelegationChange(selectedUserId)
    }
  }

  // Don't show selector if no delegations available
  if (delegationOptions.length === 0) {
    return null
  }

  return (
    <div
      className={`delegation-selector ${className}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 1rem',
        backgroundColor: '#FEF3C7',
        border: '1px solid #FCD34D',
        borderRadius: '0.375rem',
      }}
    >
      <label
        htmlFor="delegation-select"
        style={{
          fontWeight: '500',
          color: '#92400E',
          whiteSpace: 'nowrap',
        }}
      >
        Acting as:
      </label>

      <select
        id="delegation-select"
        value={activeDelegation || ''}
        onChange={handleDelegationChange}
        disabled={isLoading}
        style={{
          padding: '0.375rem 0.75rem',
          border: '1px solid #FCD34D',
          borderRadius: '0.25rem',
          backgroundColor: 'white',
          color: '#92400E',
          cursor: 'pointer',
          minWidth: '200px',
        }}
      >
        <option value="">Myself</option>
        {delegationOptions.map((option) => (
          <option key={option.userId} value={option.userId}>
            {option.userName}
          </option>
        ))}
      </select>

      {activeDelegation && (
        <span
          style={{
            fontSize: '0.875rem',
            color: '#92400E',
            fontStyle: 'italic',
          }}
        >
          (Delegated access)
        </span>
      )}
    </div>
  )
}

/**
 * Delegation Management Component
 *
 * Allows clinicians to grant/revoke delegations.
 */
interface DelegationManagementProps {
  /** Custom styling className */
  className?: string
}

export function DelegationManagement({ className = '' }: DelegationManagementProps) {
  const { availableDelegations, refreshPermissions, hasPermission } = useAuthorization()
  const { getAccessTokenSilently } = useAuth0()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Check if user can grant delegations
  const canGrantDelegations = hasPermission('delegate:grant')

  if (!canGrantDelegations) {
    return null // Don't show if user can't grant delegations
  }

  async function handleRevokeDelegation(delegationId: string) {
    if (!confirm('Are you sure you want to revoke this delegation?')) {
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const token = await getAccessTokenSilently()
      const apiBaseUrl = import.meta.env['VITE_PROVIDER_API_URL'] || import.meta.env['VITE_API_BASE_URL']

      const response = await fetch(`${apiBaseUrl}/auth/delegations/${delegationId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to revoke delegation: ${response.statusText}`)
      }

      // Refresh delegations list
      await refreshPermissions()
    } catch (err) {
      console.error('Failed to revoke delegation:', err)
      setError(err instanceof Error ? err.message : 'Failed to revoke delegation')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={`delegation-management ${className}`}>
      <h3 style={{ marginBottom: '1rem', fontSize: '1.125rem', fontWeight: '600' }}>My Delegations</h3>

      {error && (
        <div
          style={{
            padding: '0.75rem',
            backgroundColor: '#FEE2E2',
            border: '1px solid #FCA5A5',
            borderRadius: '0.375rem',
            color: '#991B1B',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      {availableDelegations.length === 0 ? (
        <p style={{ color: '#6B7280', fontStyle: 'italic' }}>No active delegations</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {availableDelegations.map((delegation) => (
            <div
              key={delegation.id}
              style={{
                padding: '1rem',
                border: '1px solid #E5E7EB',
                borderRadius: '0.5rem',
                backgroundColor: 'white',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                  <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>To: {delegation.granteeUserId}</div>
                  <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                    Permissions: {delegation.permissions.join(', ')}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6B7280', marginTop: '0.25rem' }}>
                    Status: {delegation.status}
                  </div>
                  {delegation.endsAt && (
                    <div style={{ fontSize: '0.875rem', color: '#6B7280', marginTop: '0.25rem' }}>
                      Expires: {new Date(delegation.endsAt).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {delegation.status === 'active' && (
                  <button
                    onClick={() => handleRevokeDelegation(delegation.id)}
                    disabled={isLoading}
                    style={{
                      padding: '0.375rem 0.75rem',
                      backgroundColor: '#DC2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.25rem',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                    }}
                  >
                    {isLoading ? 'Revoking...' : 'Revoke'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
